/*
 * eslint-disable @typescript-eslint/naming-convention
 *
 * Postgres 컬럼은 snake_case 이고 supabase-js 는 컬럼명을 그대로 키로 쓴다. 이 파일은 그 경계를
 * 넘는 곳(행 → 도메인 타입 변환)이라 snake_case 가 불가피하다. 규칙을 파일 단위로 끄되,
 * **변환 이후의 도메인 타입은 전부 camelCase** 라 앱 내부 규약은 그대로 유지된다.
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { getAuthedClient, supabase } from '~/common/utils/supabase.util.ts';
import { TRAINING_LOG_SEED, TUTOR_GREETING } from '~/features/matching/data/matching.data';
import { getMyProfileId } from '~/features/profile/api/profile.api';

import type { SupabaseClient } from '@supabase/supabase-js';

import type {
  ChatMessage,
  ChatRoom,
  ClassRequest,
  ClassRequestInput,
  TrainingLog,
} from '~/features/matching/types/matching.type';

/**
 * 매칭 데이터 어댑터 — Supabase 구현.
 *
 * @description 대응 테이블: `class_requests` · `chat_rooms` · `chat_messages` · `training_logs`.
 *  신원은 `auth.uid()` → `profiles.id`(= `guardian_id`) 이며 RLS 가 본인 것만 통과시킨다.
 *
 *  ## 설계상 알아둘 두 가지
 *  1. **지도사도 실제 계정이다.** `tutor_id` 는 `profiles(id)` 외래키이고 메시지 발신자도 계정
 *     (`sender_id`)이다(마이그레이션 0007 이 슬러그·역할 기반 임시 구조를 되돌렸다).
 *     그래서 "내 메시지"는 역할이 아니라 **내 프로필 id 와 같은지**로 판정한다.
 *  2. **채팅방은 DB 트리거가 만든다.** 상태가 `active` 로 바뀌는 순간 `class_requests_accept` 트리거가
 *     방을 생성한다. 앱이 따로 insert 하지 않는 이유는 "수락됐는데 방이 없는" 중간 상태를 코드 경로가
 *     아니라 **DB 차원에서** 원천 차단하기 위해서다(방 insert 정책도 일부러 두지 않았다).
 *
 *  안읽음 수는 컬럼이 아니라 `chat_messages.read_at IS NULL` 로 계산한다 — 카운터 컬럼은 메시지와
 *  어긋날 수 있어 단일 진실을 메시지 쪽에 둔다.
 */

interface RequestRow {
  id: string;
  class_id: string;
  tutor_id: string;
  guardian_id: string;
  guardian: { nickname: string; avatar_url: string | null } | null;
  pet: { name: string; photo_url: string | null } | null;
  pet_id: string | null;
  status: ClassRequest['status'];
  region_city: string;
  region_district: string;
  address_detail: string | null;
  preferred_date: string;
  concern: string | null;
  next_session_at: string | null;
  session_count: number;
  accepted_at: string | null;
  created_at: string;
}

interface RoomRow {
  id: string;
  request_id: string;
  created_at: string;
  class_requests: {
    tutor_id: string;
    guardian_id: string;
    tutor: { nickname: string; slug: string | null } | null;
    guardian: { nickname: string } | null;
  } | null;
}

interface MessageRow {
  id: string;
  room_id: string;
  sender_id: string;
  kind: ChatMessage['kind'];
  body: string | null;
  media_url: string | null;
  read_at: string | null;
  created_at: string;
}

function toRequest(row: RequestRow): ClassRequest {
  return {
    id: row.id,
    classId: row.class_id,
    tutorId: row.tutor_id,
    guardianId: row.guardian_id,
    guardianName: row.guardian?.nickname ?? '',
    guardianPhotoUrl: row.guardian?.avatar_url ?? undefined,
    petName: row.pet?.name ?? undefined,
    petPhotoUrl: row.pet?.photo_url ?? undefined,
    petId: row.pet_id ?? undefined,
    status: row.status,
    regionCity: row.region_city,
    regionDistrict: row.region_district,
    addressDetail: row.address_detail ?? undefined,
    preferredDate: row.preferred_date,
    concern: row.concern ?? undefined,
    nextSessionAt: row.next_session_at ?? undefined,
    sessionCount: row.session_count,
    acceptedAt: row.accepted_at ?? undefined,
    createdAt: row.created_at,
  };
}

/**
 * 행 → 도메인 변환.
 * @param myProfileId 내 프로필 id. 이게 있어야 좌/우 버블을 가른다 — 보호자든 지도사든
 *  **내가 보낸 것**이 우측이다(역할로 가르면 지도사 화면에서 좌우가 뒤집힌다).
 */
function toMessage(row: MessageRow, myProfileId: string | null): ChatMessage {
  return {
    id: row.id,
    roomId: row.room_id,
    sender: row.sender_id === myProfileId ? 'me' : 'other',
    kind: row.kind,
    body: row.body ?? undefined,
    mediaUrl: row.media_url ?? undefined,
    createdAt: row.created_at,
  };
}

/** 세션·프로필이 모두 준비됐을 때만 데이터에 접근한다. 아니면 조용히 빈 결과를 준다(온보딩 전 상태). */
async function withProfile<T>(
  fallback: T,
  run: (client: SupabaseClient, profileId: string) => Promise<T>,
): Promise<T> {
  const client = await getAuthedClient();
  if (!client) return fallback;
  const profileId = await getMyProfileId();
  if (!profileId) return fallback;
  return run(client, profileId);
}

/**
 * 신청 조회에 신청자·반려견을 함께 읽는다.
 * @description 지도사 콘솔은 "누가 어떤 아이로 신청했는지"가 화면의 본문이다. 신청마다 따로
 *  조회하면 요청이 신청 수만큼 늘어나므로 외래키 임베딩으로 한 번에 가져온다.
 *  profiles 를 두 번 참조하는 테이블이라 FK 이름을 명시해야 PostgREST 가 어느 관계인지 안다.
 */
const REQUEST_COLUMNS =
  '*, guardian:profiles!class_requests_guardian_id_fkey(nickname, avatar_url), pet:pets(name, photo_url)';

/** 보호자 뷰 — 내가 **넣은** 신청. */
export async function fetchRequestsApi(): Promise<ClassRequest[]> {
  return withProfile<ClassRequest[]>([], async (client, profileId) => {
    const { data, error } = await client
      .from('class_requests')
      .select(REQUEST_COLUMNS)
      .eq('guardian_id', profileId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data as unknown as RequestRow[] | null) ?? []).map(toRequest);
  });
}

/**
 * 지도사 뷰 — 나에게 **들어온** 신청.
 * @description 필터를 생략하고 RLS 에만 맡기면 지도사 계정이 보호자로 넣은 신청까지 섞여
 *  콘솔에 자기 신청이 뜬다(역할 분리 전의 그 증상이 그대로 재현된다). 축을 명시한다.
 */
export async function fetchTutorRequestsApi(): Promise<ClassRequest[]> {
  return withProfile<ClassRequest[]>([], async (client, profileId) => {
    const { data, error } = await client
      .from('class_requests')
      .select(REQUEST_COLUMNS)
      .eq('tutor_id', profileId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data as unknown as RequestRow[] | null) ?? []).map(toRequest);
  });
}

export async function createRequestApi(input: ClassRequestInput): Promise<ClassRequest> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');
  const profileId = await getMyProfileId();
  // 신청은 보호자 프로필에 매달린다 — 온보딩을 안 하면 저장할 주체가 없다.
  if (!profileId) throw new Error('먼저 프로필을 등록해주세요.');

  const { data, error } = await client
    .from('class_requests')
    .insert({
      class_id: input.classId,
      tutor_id: input.tutorId,
      guardian_id: profileId,
      pet_id: input.petId ?? null,
      region_city: input.regionCity,
      region_district: input.regionDistrict,
      address_detail: input.addressDetail ?? null,
      preferred_date: input.preferredDate,
      concern: input.concern ?? null,
    })
    .select(REQUEST_COLUMNS)
    .single();

  if (error) throw error;
  return toRequest(data as unknown as RequestRow);
}

export async function cancelRequestApi(requestId: string): Promise<ClassRequest> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');

  const { data, error } = await client
    .from('class_requests')
    .update({ status: 'cancelled' })
    .eq('id', requestId)
    .select(REQUEST_COLUMNS)
    .single();

  if (error) throw error;
  return toRequest(data as unknown as RequestRow);
}

/**
 * 지도사 수락 — 상태를 `active` 로 올리면 **트리거가 방을 만든다.**
 * @description 방 생성 후 인사 메시지와 훈련 기록 시드를 넣어 "수락 = 대화 시작"이 화면에서 즉시 보이게 한다.
 */
export async function acceptRequestApi(
  requestId: string,
  context: { tutorName: string; className: string },
): Promise<{ request: ClassRequest; room: ChatRoom }> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');

  const next = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();

  const { data: updated, error: updateError } = await client
    .from('class_requests')
    .update({ status: 'active', next_session_at: next })
    .eq('id', requestId)
    .select(REQUEST_COLUMNS)
    .single();
  if (updateError) throw updateError;

  const { data: roomRow, error: roomError } = await client
    .from('chat_rooms')
    .select('id, request_id, created_at')
    .eq('request_id', requestId)
    .single();
  if (roomError) throw roomError;

  const room = roomRow as { id: string; request_id: string; created_at: string };
  const request = toRequest(updated as unknown as RequestRow);

  const myProfileId = await getMyProfileId();
  const { error: messageError } = await client.from('chat_messages').insert({
    room_id: room.id,
    sender_id: myProfileId,
    kind: 'text',
    body: TUTOR_GREETING(context.tutorName, context.className),
  });
  if (messageError) throw messageError;

  const { error: logError } = await client.from('training_logs').insert(
    TRAINING_LOG_SEED.map(seed => ({
      request_id: requestId,
      logged_on: '2020-01-20',
      memo: seed.memo ?? null,
    })),
  );
  if (logError) throw logError;

  return {
    request,
    room: {
      id: room.id,
      requestId: room.request_id,
      tutorId: request.tutorId,
      guardianId: request.guardianId,
      // 수락한 주체가 지도사이므로 이 응답에서의 상대는 신청한 보호자다.
      counterpartName: request.guardianName,
      unreadCount: 1,
      createdAt: room.created_at,
    },
  };
}

/**
 * 수업 완료 처리 — 지도사만.
 * @description 완료는 후기 작성 자격의 근거다(`reviews` 정책이 `completed` 를 요구한다).
 *  그래서 "누가 완료를 누를 수 있는가"가 곧 "누가 평점을 만들 수 있는가"가 된다 —
 *  보호자가 스스로 완료 처리하지 못하도록 DB 트리거가 전이를 막는다(마이그레이션 0014).
 */
export async function completeRequestApi(requestId: string): Promise<ClassRequest> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');

  const { data, error } = await client
    .from('class_requests')
    .update({ status: 'completed' })
    .eq('id', requestId)
    .select(REQUEST_COLUMNS)
    .single();

  if (error) throw error;
  return toRequest(data as unknown as RequestRow);
}

export async function fetchRoomsApi(): Promise<ChatRoom[]> {
  return withProfile<ChatRoom[]>([], async (client, profileId) => {
    const { data, error } = await client
      .from('chat_rooms')
      /*
        상대 이름을 방 목록에서 바로 보여주려면 프로필까지 함께 읽어야 한다. 방 하나당 따로 조회하면
        요청이 방 수만큼 늘어나므로 외래키 임베딩으로 한 번에 가져온다(FK 이름을 명시해야 하는 이유는
        class_requests 가 profiles 를 두 번 참조해 PostgREST 가 어느 쪽인지 모르기 때문).
      */
      .select(
        'id, request_id, created_at, class_requests!inner(tutor_id, guardian_id, tutor:profiles!class_requests_tutor_id_fkey(nickname, slug), guardian:profiles!class_requests_guardian_id_fkey(nickname))',
      )
      .order('created_at', { ascending: false });
    if (error) throw error;

    const rooms = (data as unknown as RoomRow[] | null) ?? [];
    if (rooms.length === 0) return [];

    // 안읽음은 메시지에서 센다 — 방에 카운터를 두면 메시지와 어긋날 여지가 생긴다.
    // 안읽음 = 내가 보내지 않은 메시지 중 아직 안 읽은 것(역할이 아니라 발신자로 판정).
    const { data: unreadRows, error: unreadError } = await client
      .from('chat_messages')
      .select('room_id')
      .is('read_at', null)
      .neq('sender_id', profileId);
    if (unreadError) throw unreadError;

    const unreadByRoom = ((unreadRows as { room_id: string }[] | null) ?? []).reduce<
      Record<string, number>
    >((acc, row) => {
      acc[row.room_id] = (acc[row.room_id] ?? 0) + 1;
      return acc;
    }, {});

    return rooms.map(row => {
      // 상대는 "역할"이 아니라 "내가 아닌 쪽"이다 — 같은 화면을 보호자·지도사가 함께 쓴다.
      const isMineAsTutor = row.class_requests?.tutor_id === profileId;
      return {
        id: row.id,
        requestId: row.request_id,
        tutorId: row.class_requests?.tutor_id ?? '',
        guardianId: row.class_requests?.guardian_id ?? '',
        counterpartName: isMineAsTutor
          ? (row.class_requests?.guardian?.nickname ?? '')
          : (row.class_requests?.tutor?.nickname ?? ''),
        counterpartTutorSlug: isMineAsTutor
          ? undefined
          : (row.class_requests?.tutor?.slug ?? undefined),
        unreadCount: unreadByRoom[row.id] ?? 0,
        createdAt: row.created_at,
      };
    });
  });
}

export async function fetchMessagesApi(roomId: string): Promise<ChatMessage[]> {
  return withProfile<ChatMessage[]>([], async (client, profileId) => {
    const { data, error } = await client
      .from('chat_messages')
      .select('*')
      .eq('room_id', roomId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return ((data as MessageRow[] | null) ?? []).map(row => toMessage(row, profileId));
  });
}

export async function sendMessageApi(roomId: string, body: string): Promise<ChatMessage> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');
  const profileId = await getMyProfileId();
  // RLS 가 `sender_id = 내 프로필` 인 행만 통과시킨다 — 프로필이 없으면 애초에 쓸 수 없다.
  if (!profileId) throw new Error('먼저 프로필을 등록해주세요.');

  const { data, error } = await client
    .from('chat_messages')
    .insert({ room_id: roomId, sender_id: profileId, kind: 'text', body })
    .select('*')
    .single();

  if (error) throw error;
  return toMessage(data as MessageRow, profileId);
}

/** 방 진입 시 상대 메시지를 읽음 처리 — 목록의 코랄 뱃지가 사라지는 근거. */
export async function markRoomReadApi(roomId: string): Promise<ChatRoom[]> {
  const client = await getAuthedClient();
  const profileId = await getMyProfileId();
  if (client && profileId) {
    const { error } = await client
      .from('chat_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('room_id', roomId)
      .neq('sender_id', profileId)
      .is('read_at', null);
    if (error) throw error;
  }
  // 갱신된 안읽음 수를 반영한 목록을 그대로 돌려준다(슬라이스가 통째로 교체한다).
  return fetchRoomsApi();
}

/**
 * 채팅방 실시간 구독 — 새 메시지가 들어오면 콜백으로 밀어준다.
 *
 * @returns 구독 해제 함수. **반드시 호출해야 한다** — 방을 옮길 때마다 채널이 쌓이면 같은 메시지가
 *  여러 번 도착한다.
 * @description 폴링 대신 Postgres 논리 복제를 타는 방식이라 지연이 거의 없고 빈 요청도 나가지 않는다.
 *  RLS 가 Realtime 에도 적용되므로 남의 방을 구독해도 아무것도 오지 않는다(발행 설정은 마이그레이션 0005).
 *
 *  ⚠️ 내가 보낸 메시지도 이벤트로 되돌아온다. 전송 응답으로 이미 목록에 넣었으므로 호출부(슬라이스)가
 *  id 로 중복을 걸러야 한다 — 여기서 거르지 않는 이유는 "무엇이 이미 있는지"를 아는 쪽이 스토어이기 때문이다.
 */
export function subscribeMessages(
  roomId: string,
  myProfileId: string | null,
  onInsert: (message: ChatMessage) => void,
) {
  // 지역 변수로 고정한다 — cleanup 클로저 안에서는 모듈 변수의 null 좁힘이 유지되지 않는다.
  const client = supabase;
  if (!client) return () => {};

  const channel = client
    .channel(`chat:${roomId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `room_id=eq.${roomId}` },
      payload => {
        onInsert(toMessage(payload.new as MessageRow, myProfileId));
      },
    )
    .subscribe();

  return () => {
    client.removeChannel(channel).catch(() => {
      // 이미 끊긴 채널을 다시 끊는 경우라 무시해도 안전하다(언마운트 경합).
    });
  };
}

export async function fetchTrainingLogsApi(requestId: string): Promise<TrainingLog[]> {
  return withProfile<TrainingLog[]>([], async client => {
    const { data, error } = await client
      .from('training_logs')
      .select('id, request_id, logged_on, memo')
      .eq('request_id', requestId)
      .order('logged_on', { ascending: false });
    if (error) throw error;

    return (
      (data as
        | { id: string; request_id: string; logged_on: string; memo: string | null }[]
        | null) ?? []
    ).map(row => ({
      id: row.id,
      requestId: row.request_id,
      // 시안 표기(2020.01.20)에 맞춰 화면에서 쓰는 형태로 바꾼다.
      loggedOn: row.logged_on.replace(/-/g, '.'),
      memo: row.memo ?? undefined,
      media: [],
    }));
  });
}
